'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Clock, ArrowRight } from 'lucide-react';
import { Title } from '@/components/ui/Headings';

interface Article {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  category: string;
  readTime: string;
  author: string;
}

interface JournalArticlesFilterProps {
  articles: Article[];
  categories: string[];
  locale: string;
  translations: {
    by: string;
    readMore: string;
  };
}

export default function JournalArticlesFilter({
  articles,
  categories,
  locale,
  translations
}: JournalArticlesFilterProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>(categories[0]);

  const filteredArticles = useMemo(() => {
    if (selectedCategory === categories[0]) {
      // "All Articles" is selected
      return articles;
    }
    return articles.filter(article => article.category === selectedCategory);
  }, [articles, selectedCategory, categories]);

  return (
    <>
      {/* Category Filter */}
      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="pt-4 md:pt-6 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredArticles.map((article, index) => (
                <Link
                  key={index}
                  href={`/${locale}/journal/${article.slug}`}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 group cursor-pointer block"
                >
                  <div className="relative overflow-hidden h-48">
                    <Image
                      src={article.image}
                      alt={article.title}
                      fill
                      className="object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 text-primary-600 px-3 py-1 rounded-full text-xs font-medium">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-xs text-gray-500 mb-3">
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{article.readTime}</span>
                      <span className="mx-2">•</span>
                      <span>{translations.by} {article.author}</span>
                    </div>
                    <Title size="sm" className="mb-3 group-hover:text-primary-700 transition-colors leading-tight">
                      {article.title}
                    </Title>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-primary-600 font-medium text-sm group-hover:text-primary-700 transition-colors">
                        {translations.readMore}
                      </span>
                      <ArrowRight className="h-4 w-4 text-primary-600 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No articles found in this category.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

